import { Component, Input, SimpleChanges, Output, EventEmitter, HostListener } from '@angular/core';
import Chart, { ActiveElement, ChartEvent, Plugin } from 'chart.js/auto';
import { DataService } from '../../../../../../services/dashboard/data.service';
import { DataPoint, NtChartRegionInterface, StartingPoint } from '../../../../../../shared/interfaces/nt-chart-region.interface';
import { ModalOpenToMarketCurve30DaysComponent } from '../modal-open-to-market-curve-30-days/modal-open-to-market-curve-30-days.component';
import { CommonModule } from '@angular/common';

type Pt = { month: string | Date; value: number };

@Component({
  selector: 'app-open-to-market-curve-30-days-merged',
  imports: [ModalOpenToMarketCurve30DaysComponent, CommonModule],
  templateUrl: './open-to-market-curve-30-days-merged.component.html',
  styleUrl: './open-to-market-curve-30-days-merged.component.css',
    host: { style: 'display: block; height: 100%; width: 100%;' }  // to ensure the component fills the card area
})
export class OpenToMarketCurve30DaysMergedComponent {

  public chart: any;
  public hasData: boolean = false;

  public chartData: NtChartRegionInterface = {
    labels: [],
    actual: [],
    real: [],
    target: []
  };

  @Input() lineColor1: string | undefined;
  @Input() lineColor2: string | undefined;
  // title selected in the card-widget header (displayedTitle), shown in the modal
  @Input() widgetTitle: string = '';
  @Output() hasOpenToMarketCurveData = new EventEmitter<boolean>();


  curvePointData: any = {};
  isMarketCurveOpen: boolean = false;

  overlappingCandidates: any[] = [];
  isOverlapChoiceOpen: boolean = false;
  overlapChoicePosition: { x: number; y: number } = { x: 0, y: 0 };

  private readonly OVERLAP_THRESHOLD_BREAKPOINT = 1516;
  private readonly OVERLAP_THRESHOLD_PERCENT_SMALL = 5;
  private readonly OVERLAP_THRESHOLD_PERCENT_LARGE = 2;

  public medicineId = 0;
  public countryId = 1;

  public chartInstances: Chart[] = [];
  public chartContext: any;
  public chartId: string;

  public isTabletView: boolean = false;
  totalMonths: number = 0;

  overlapMenuAlign: string = "right"; // default alignment

  // Built from getOpenToMarketChartInfo30Days, same processing as the 30 days curve.
  // labels -> "Mese 0", "Mese 1"...; monthLabels -> "Month N" behind each label.
  labels: string[] = [];
  actual: (number | null)[] = [];
  forecast: (number | null)[] = [];
  monthLabels: string[] = [];

  // Real start/end date of each "Month N" period, per series (each has its own Step 0)
  private forecastPeriodDates: Map<number, { start: string; end: string }> = new Map();
  private actualPeriodDates: Map<number, { start: string; end: string }> = new Map();

  constructor(private dataService: DataService) {
    this.chartId = 'chart_' + Math.random().toString(36).substring(2, 9);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isOverlapChoiceOpen) {
      const target = event.target as HTMLElement;
      const dropdownElement = document.querySelector('.overlap-dropdown-menu');
      if (dropdownElement && !dropdownElement.contains(target)) {
        this.closeOverlapChoice();
      }
    }
  }

  ngAfterViewInit(): void {
    this.medicineId = sessionStorage.getItem('medicineId') ? Number(sessionStorage.getItem('medicineId')) : 0;
    this.countryId = sessionStorage.getItem('countryId') ? Number(sessionStorage.getItem('countryId')) : 1;
    this.isTabletView = window.innerWidth < 1526; // TABLET VIEW
    window.addEventListener('resize', () => this.onResize());

    this.loadChartData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['medicineId']) {
      this.loadChartData();  // Ricarica il grafico quando cambia il medicineId o countryId
    }
  }

  ngOnDestroy(): void {
    this.destroyCharts();
  }

  private onResize(): void {
    const newWidth = window.innerWidth;
    const wasTablet = this.isTabletView;
    this.isTabletView = newWidth < 1526; // TABLET VIEW

  }

  loadChartData(): void {
    setTimeout(() => {
      /*this.dataService.getNationalMarketChart(this.medicineId, this.countryId).subscribe(data => {
        // verify data
        this.hasData = data?.labels?.length > 0 &&
          (data.real.some((val: any) => val !== null) ||
            data.target.some((val: any) => val !== null));

        if (!this.hasData) {
          this.destroyCharts();
          this.createEmptyChart();
          return;
        }*/

      /*this.chartData = {
        ...data,
        labels: data.labels.map((label: string) => {
          const [year, month] = label.split('-');
          return `${month}-${year}`;
        })
      };*/

      /*this.chartData = data

      console.log('chart data', this.chartData);


      this.destroyCharts();
      this.createChart();
    });*/

      this.dataService.getOpenToMarketChartInfo30Days(this.medicineId, this.countryId).subscribe(infoData => {
        // verify data
        this.hasData = infoData?.labels?.length > 0 &&
          (infoData.actual.some((val: any) => val !== null) ||
            infoData.target.some((val: any) => val !== null));
        // USE THIS TO SIMULATE NO DATA SCENARIO
        // this.hasData=false;
        if (!this.hasData) {
          this.hasOpenToMarketCurveData.emit(false);
          this.destroyCharts();
          this.createEmptyChart();
          return;
        }

        this.hasOpenToMarketCurveData.emit(true);
        this.chartData = infoData
        this.buildSeriesFromData(infoData);
        this.destroyCharts();
        this.createChart();
      });

    });
  }

  private formatDateDDMMYYYY(dateStr: string): string {
    // "yyyy-MM-dd" is read as a local calendar date: new Date("yyyy-MM-dd") is UTC
    // and can show the previous day in timezones behind UTC.
    const date = this.parseYmd(dateStr) ?? new Date(dateStr);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${date.getFullYear()}`;
  }

  // ---- Calendar-date helpers (local dates at midnight, no UTC conversion) ----

  // "yyyy-MM-dd" (optionally followed by a time) -> local date, or null if not valid.
  private parseYmd(dateStr: string | null | undefined): Date | null {
    const m = String(dateStr ?? '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return date.getMonth() === Number(m[2]) - 1 ? date : null;
  }

  private toYmd(date: Date): string {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${mm}-${dd}`;
  }

  // Step 0 + n calendar months, always anchored on Step 0 and clamped to the end of the month.
  private addMonthsFromStart(start: Date, n: number): Date {
    const lastDay = new Date(start.getFullYear(), start.getMonth() + n + 1, 0).getDate();
    return new Date(start.getFullYear(), start.getMonth() + n, Math.min(start.getDate(), lastDay));
  }

  // Backend rule: Month 1 = [Step 0, Step 0 + 1 month],
  // Month N = [Step 0 + (N-1) months + 1 day, Step 0 + N months]. Month 0 has no range.
  private periodRange(start: Date, n: number): { start: Date; end: Date } {
    const end = this.addMonthsFromStart(start, n);
    if (n === 1) return { start, end };
    const prevEnd = this.addMonthsFromStart(start, n - 1);
    return { start: new Date(prevEnd.getFullYear(), prevEnd.getMonth(), prevEnd.getDate() + 1), end };
  }

  // Period index today falls into: -1 if Step 0 is still in the future, otherwise N >= 1.
  private currentPeriodIndex(start: Date): number {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (today < start) return -1;
    let n = 1;
    while (this.addMonthsFromStart(start, n) < today) n++;
    return n;
  }

  // TEST: month column under the cursor (yellow band) and its popup (hidden while on a dot)
  hoverColumnIndex: number | null = null;
  columnPopup: {
    title: string;
    rows: { type: string; percent: string | null; value: string; color: string | undefined }[];
  } | null = null;
  columnPopupPosition: { x: number; y: number } = { x: 0, y: 0 };
  columnPopupAlign: 'left' | 'right' = 'left';

  private updateColumnHover(event: ChartEvent, activeElements: ActiveElement[], chart: Chart): void {
    const { left, right, top, bottom } = chart.chartArea;
    const insideArea = event.x !== null && event.y !== null &&
      event.x >= left && event.x <= right && event.y >= top && event.y <= bottom;

    const columnItems = insideArea
      ? chart.getElementsAtEventForMode(event.native as Event, 'index', { intersect: false }, false)
      : [];
    const index = columnItems.length > 0 ? columnItems[0].index : null;

    if (index !== this.hoverColumnIndex) {
      this.hoverColumnIndex = index;
      chart.draw();
    }

    // On a dot the normal single point tooltip is shown, so the column popup hides
    if (index === null || activeElements.length > 0) {
      this.columnPopup = null;
      return;
    }

    // Date range covered by this month for each series (each has its own Step 0; Month 0 has none)
    const periodIndex = this.periodIndexAt(index);

    // Percentage of the series at this month (null when the series has no point here)
    const formatPercent = (v: number | null | undefined): string | null =>
      v === null || v === undefined ? null : `${Number(v).toFixed(2)}%`;

    const rows = [
      {
        type: 'Forecast',
        period: this.forecastPeriodDates.get(periodIndex),
        percent: formatPercent(this.forecast[index]),
        color: this.lineColor2,
      },
      {
        type: 'Actual',
        period: this.actualPeriodDates.get(periodIndex),
        percent: formatPercent(this.actual[index]),
        color: this.lineColor1,
      },
    ]
      .filter(r => r.period)
      .map(r => ({
        type: r.type,
        percent: r.percent,
        value: `${this.formatDateDDMMYYYY(r.period!.start)} - ${this.formatDateDDMMYYYY(r.period!.end)}`,
        color: r.color,
      }));

    if (rows.length === 0) {
      this.columnPopup = null;
      return;
    }

    this.columnPopup = { title: this.labels[index], rows };
    this.columnPopupPosition = { x: event.x! + 12, y: event.y! + 12 };
    this.columnPopupAlign = index >= this.labels.length - 2 ? 'right' : 'left';
  }

  onChartMouseLeave(): void {
    this.columnPopup = null;
    if (this.hoverColumnIndex !== null) {
      this.hoverColumnIndex = null;
      this.chartInstances.forEach(c => c.draw());
    }
  }

  // TEST: draws a light yellow band behind the lines, over the full height of the hovered month column
  private readonly columnHighlightPlugin: Plugin<'line'> = {
    id: 'columnHighlight',
    beforeDatasetsDraw: (chart) => {
      const index = this.hoverColumnIndex;
      if (index === null) return;

      const xScale = chart.scales['x'];
      const { top, bottom, left, right } = chart.chartArea;
      const x = xScale.getPixelForValue(index);

      // Column = halfway to the previous month and halfway to the next one
      const count = chart.data.labels?.length ?? 0;
      const step = count > 1 ? Math.abs(xScale.getPixelForValue(1) - xScale.getPixelForValue(0)) : right - left;
      const start = Math.max(left, x - step / 2);
      const end = Math.min(right, x + step / 2);

      const ctx = chart.ctx;
      ctx.save();
      ctx.fillStyle = 'rgba(255, 230, 120, 0.12)';
      ctx.fillRect(start, top, end - start, bottom - top);
      ctx.restore();
    }
  };

  // "Month N" behind the point at dataIndex -> N
  private periodIndexAt(dataIndex: number): number {
    return Number(this.monthLabels[dataIndex]?.match(/(\d+)/)?.[1]);
  }

  /* same processing as the 30 days curve: labels are relative ("Month 0", "Month 1"...),
     cumulative values, carry-forward, Actual stops at the current period and once it reaches 100%. */
  private buildSeriesFromData(chartData: NtChartRegionInterface): void {
    const monthIndexOf = (input: string | Date): number => {
      const raw = String(input).trim();
      const m = raw.match(/(\d+)/);
      if (!m) throw new Error(`Bad month format: ${raw}`);
      return Number(m[1]);
    };

    const accumulate = (points: Pt[]) => {
      const sums = new Map<number, number>();
      for (const p of points) {
        const k = monthIndexOf(p.month);
        const roundedValue = Math.round(Number(p.value) * 100) / 100;
        sums.set(k, (sums.get(k) ?? 0) + roundedValue);
      }
      const months = [...sums.keys()].sort((a, b) => a - b);
      const acc = new Map<number, number>();
      let running = 0;
      for (const m of months) {
        running += sums.get(m)!;
        running = Math.round(running * 100) / 100;
        acc.set(m, running);
      }
      return acc;
    };

    const firstMonthNum = (acc: Map<number, number>) => {
      if (acc.size === 0) return null;
      return Math.min(...acc.keys());
    };

    const buildSeries = (
      labels: string[],
      acc: Map<number, number>,
      carryForward: boolean,
      maxMonthNum?: number, // for Actual: stop at current period
      stopAt100?: boolean,  // for Actual: stop inserting points once value reaches 100
      zeroBeforeStart?: boolean, // series has a known Step 0: periods before the first value are 0%
    ): (number | null)[] => {
      const startNum = firstMonthNum(acc);
      const out: (number | null)[] = [];
      let last: number | undefined;
      let reachedMax = false;

      for (const label of labels) {
        const num = monthIndexOf(label);

        if (!zeroBeforeStart && (startNum == null || num < startNum)) {
          out.push(null);
          continue;
        }
        if (maxMonthNum != null && num > maxMonthNum) {
          out.push(null);
          continue;
        }
        if (stopAt100 && reachedMax) {
          out.push(null);
          continue;
        }

        const v = acc.get(num);
        if (v != null) {
          out.push(v);
          last = v;
          if (stopAt100 && v >= 100) reachedMax = true;
        } else if (carryForward && last != null) {
          out.push(last);
          if (stopAt100 && last >= 100) reachedMax = true;
        } else if (zeroBeforeStart) {
          out.push(0);
          last = 0;
        } else {
          out.push(null);
        }
      }
      return out;
    };

    // Step 0 of each series (optional API fields). Each series only uses its own Step 0.
    const forecastStart = this.parseYmd(chartData.forecastStartDate);
    const actualStart = this.parseYmd(chartData.actualStartDate);

    // Current Actual period: from actualStartDate if known, otherwise from the period dates sent with the points
    const findCurrentMonthIndex = (): number | undefined => {
      if (actualStart) return this.currentPeriodIndex(actualStart);
      const apiDates: { idx: number; start: string }[] = (chartData.actual || [])
        .filter((p: any) => p.startPeriodDate && p.endPeriodDate)
        .map((p: any) => ({ idx: monthIndexOf(p.month), start: p.startPeriodDate }));
      if (apiDates.length === 0) return undefined;
      const today = new Date();
      let cap = -1;
      apiDates.forEach(({ idx, start }) => {
        if (new Date(start) <= today && idx > cap) cap = idx;
      });
      return cap;
    };
    const currentMonthIndex = findCurrentMonthIndex();

    // Every period from Month 0 to the last one needed by either series
    const allIndexes = [
      ...(chartData.labels || []),
      ...(chartData.target || []).map(p => p.month),
      ...(chartData.actual || []).map(p => p.month),
    ].map(monthIndexOf);
    const maxIndex = Math.max(0, ...allIndexes, currentMonthIndex ?? 0);
    const labels: string[] = [];
    for (let i = 0; i <= maxIndex; i++) labels.push('Month ' + i);

    // Real dates behind each "Month N" of each series, for the tooltip and the modal
    const collectPeriodDates = (
      start: Date | null,
      points: any[],
      target: Map<number, { start: string; end: string }>,
    ) => {
      target.clear();
      if (start) {
        for (let i = 1; i <= maxIndex; i++) {
          const range = this.periodRange(start, i);
          target.set(i, { start: this.toYmd(range.start), end: this.toYmd(range.end) });
        }
        return;
      }
      points.forEach((p: any) => {
        const idx = monthIndexOf(p.month);
        if (idx > 0 && p.startPeriodDate && p.endPeriodDate) {
          target.set(idx, { start: p.startPeriodDate, end: p.endPeriodDate });
        }
      });
    };
    collectPeriodDates(forecastStart, chartData.target || [], this.forecastPeriodDates);
    collectPeriodDates(actualStart, chartData.actual || [], this.actualPeriodDates);

    this.forecast = buildSeries(labels, accumulate(chartData.target || []), /*carryForward*/ true,
      /*maxMonthNum*/ undefined, /*stopAt100*/ false, /*zeroBeforeStart*/ forecastStart != null);
    this.actual = buildSeries(labels, accumulate(chartData.actual || []), /*carryForward*/ true,
      /*maxMonthNum*/ currentMonthIndex, /*stopAt100*/ true, /*zeroBeforeStart*/ actualStart != null);
    this.labels = labels.map(label => 'Mese ' + monthIndexOf(label));
    this.monthLabels = labels;
  }

  private createEmptyChart(): void {
    const chartCanvas = document.getElementById(this.chartId) as HTMLCanvasElement;
    if (!chartCanvas) return;

    chartCanvas.style.width = '100%';
    chartCanvas.style.height = '280px';
    chartCanvas.style.minWidth = '300px';

    this.chartContext = chartCanvas.getContext('2d');

    const emptyChart = new Chart(this.chartContext, {
      type: 'line',
      data: {
        labels: ['', '', '', '', '', ''],
        datasets: [
          {
            label: "\u00A0Actual",
            data: [null, null, null, null, null, null],
            borderColor: 'rgba(0, 128, 255, 0.2)',
            backgroundColor: 'rgba(0, 128, 255, 0.1)',
            borderWidth: 2,
            tension: 0.2,
            pointRadius: this.isTabletView ? 3.6 : 4.5,
            pointHoverRadius: this.isTabletView ? 5.5 : 7.5,
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
            spanGaps: false,
            clip: false,
            fill: false
          },
          {
            label: "\u00A0Forecast\u00A0\u00A0\u00A0\u00A0",
            data: [null, null, null, null, null, null],
            borderColor: 'rgba(255, 165, 0, 0.2)',
            backgroundColor: 'rgba(255, 165, 0, 0.1)',
            borderWidth: 2,
            tension: 0.2,
            pointRadius: this.isTabletView ? 3.6 : 4.5,
            pointHoverRadius: this.isTabletView ? 5.5 : 7.5,
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
            spanGaps: false,
            clip: false,
            fill: false
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: { enabled: false }
        },
        scales: {
          x: {
            display: true,
            grid: {
              display: false,
              drawOnChartArea: false,
              drawTicks: false
            },
            ticks: {
              display: false
            },
            border: {
              display: false
            }
          },
          y: {
            display: true,
            min: 0,
            max: 100,
            ticks: {
              stepSize: 10,
              font: {
                size: this.isTabletView ? 6 : 8,
                family: 'Montserrat, sans-serif'
              },
              color: 'rgba(0,0,0,0.2)',
              callback: (val) => val + '%'
            },
            grid: {
              display: true,
              color: 'rgba(0,0,0,0.05)',
              drawTicks: false,
              lineWidth: 1
            },
            border: {
              display: false
            }
          }
        },
        onClick: () => false,
        elements: {
          line: {
            borderWidth: 2
          },
          point: {
            radius: 0
          }
        },
        layout: {
          padding: {
            top: 10,
            bottom: 10
          }
        }
      }
    });

    this.chartInstances.push(emptyChart);
  }


  createChart() {
    const chartCanvas = document.getElementById(this.chartId) as HTMLCanvasElement;

    this.totalMonths = this.labels.length;
    if (!chartCanvas) {
      console.error("Elemento canvas non trovato");
      return;
    } else {
      this.chartContext = chartCanvas.getContext("2d");
    }

    if (!this.chartData || !this.chartData.labels || this.chartData.labels.length === 0) {
      const chartEmptyInstance = new Chart(this.chartContext, {
        type: 'line',
        data: {
          labels: ['Nessun Dato'],
          datasets: [
            {
              label: '',
              data: [],
              backgroundColor: ['#d3d3d3'],
              borderWidth: 1,
              clip: false
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          aspectRatio: 5.0,
          plugins: {
            legend: {
              display: false
            }
          },
          scales: {
            y: {
              display: true,
              title: {
                display: false,
                text: 'Valori'
              },
              min: 0,
              max: 100,
              ticks: {
                callback: function (data, index, values) {
                  return data + '%';
                },
                font: {
                  size: this.isTabletView ? 6 : 8,
                  family: 'Montserrat, sans-serif'
                },
                stepSize: 10,
                maxTicksLimit: 11
              },
              suggestedMin: 0,
              suggestedMax: 100,
              grid: {
                display: true
              },
              border: {
                display: false
              },
            }
          }
        }
      });


      this.chartInstances.push(chartEmptyInstance);
      console.error("Dati non disponibili.");
      return;
    }

    // Series already built by buildSeriesFromData (same processing as the 30 days curve)
    const actualValues = this.actual;
    const targetValues = this.forecast;

    const chartInstance = new Chart(this.chartContext, {
      type: 'line',
      data: {
        labels: this.labels, // "Mese 0", "Mese 1"...
        datasets: [
          {
            label: "\u00A0Actual",
            data: actualValues,
            borderColor: this.lineColor1,
            backgroundColor: this.lineColor1,
            borderWidth: 2,
            clip: false,
            fill: false,
            tension: 0.2,
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
            pointRadius: this.isTabletView ? 4.3 : 5,
            pointHoverRadius: this.isTabletView ? 6.5 : 7.5,
            pointHitRadius: this.isTabletView ? 15 : 13,
            spanGaps: false
          },
          {
            label: "\u00A0Forecast\u00A0\u00A0\u00A0\u00A0",
            data: targetValues,
            borderColor: this.lineColor2,
            backgroundColor: this.lineColor2,
            borderWidth: 2,
            clip: false,
            fill: false,
            tension: 0.2,
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
            pointRadius: this.isTabletView ? 4.3 : 5,
            pointHoverRadius: this.isTabletView ? 6.5 : 7.5,
            pointHitRadius: this.isTabletView ? 15 : 13,
            spanGaps: false
          },
        ]
      },
      options: {
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
          legend: {
            display: false,
            position: 'top',
            align: 'center',
            reverse: true,

            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              boxWidth: 8,
              boxHeight: 8,
              font: {
                size: 13,
                family: 'Montserrat, sans-serif'
              },
              color: "#000",
            },

          },
          // Single point tooltip (only when the cursor is on the dot)
          tooltip: {
            callbacks: {
              title: (tooltipItems) => {
                const dataIndex = tooltipItems[0]?.dataIndex;
                if (dataIndex === undefined) return '';

                // Actual is dataset 0, Forecast is dataset 1: each shows only its own dates.
                const periodDates = tooltipItems[0].datasetIndex === 0 ? this.actualPeriodDates : this.forecastPeriodDates;
                const period = periodDates.get(this.periodIndexAt(dataIndex));

                return period
                  ? [this.formatDateDDMMYYYY(period.start), this.formatDateDDMMYYYY(period.end)]
                  : [];
              },
              label: function (context) {
                const value = Number(context.raw);
                return ` ${value.toFixed(2)}%`;
              }
            }
          }
        },
        // TEST: yellow column + column popup on hover anywhere in the month column
        onHover: (event, activeElements, chart) => this.updateColumnHover(event, activeElements, chart),
        scales: {
          x: {
            display: true, // Mostra l'asse X
            title: {
              display: false, // Mostra il titolo sull'asse X
              text: 'Mesi' // Testo del titolo sull'asse X
            },
            grid: {
              display: false, // Nascondi le linee della griglia verticali
              lineWidth: 0,
              color: 'rgba(0, 0, 0, 0)',
              drawOnChartArea: false,
            },
            ticks: {
              font: {
                size: this.isTabletView ? (this.totalMonths > 15 ? 6.6 : 7) : 9,
                family: 'Montserrat, sans-serif'
              },
            },
          },
          y: {
            display: true, // Mostra l'asse Y
            title: {
              display: false, // Mostra il titolo sull'asse Y
              text: 'Valori' // Testo del titolo sull'asse Y
            },
            min: 0,
            max: 100,
            ticks: {
              callback: function (data, index, values) {
                return data + '%'; // Converti il valore decimale in percentuale e aggiungi il simbolo "%"
              },
              font: {
                size: this.isTabletView ? 7 : 9,  // Imposta il font-size delle etichette
                family: 'Montserrat, sans-serif'
              },
              stepSize: 10,
              maxTicksLimit: 11
            },
            suggestedMin: 0,
            suggestedMax: 100,
            grid: {
              display: true
            },
            border: {
              display: false
            },
          }
        },
        onClick: (event, _elements, chart) => {
          // Click still works only on the point itself (the column-wide interaction is for the tooltip)
          const elements = chart.getElementsAtEventForMode(event.native as Event, 'nearest', { intersect: true }, true);
          if (elements.length === 0) return;

          const clickedElement = elements[0];
          const datasetIndex = clickedElement.datasetIndex;
          const dataIndex = clickedElement.index;

          if (dataIndex >= this.monthLabels.length) return;

          const selectedPeriod = this.periodIndexAt(dataIndex);
          const actualVal = actualValues[dataIndex];
          const targetVal = targetValues[dataIndex];

          // Same payload as the 30 days curve: period index + real dates of that series
          const buildCandidate = (type: 'Actual' | 'Forecast') => {
            const period = (type === 'Actual' ? this.actualPeriodDates : this.forecastPeriodDates).get(selectedPeriod);
            return {
              type,
              selectedPeriod,
              value: type === 'Actual' ? actualVal : targetVal,
              startPeriodDate: period?.start ?? null,
              endPeriodDate: period?.end ?? null,
            };
          };

          const clickedCandidate = buildCandidate(datasetIndex === 0 ? 'Actual' : 'Forecast');
          const otherCandidate = buildCandidate(datasetIndex === 0 ? 'Forecast' : 'Actual');

          const bothValuesExist =
            actualVal !== null && actualVal !== undefined &&
            targetVal !== null && targetVal !== undefined;

          const difference = bothValuesExist
            ? Math.abs((actualVal as number) - (targetVal as number))
            : Infinity;

          if (bothValuesExist && difference <= this.getOverlapThresholdPercent()) {
            this.overlappingCandidates = [clickedCandidate, otherCandidate];
            const isLastPoint = dataIndex >= (this.chartData.labels?.length ?? 0) - 2;
this.overlapMenuAlign = isLastPoint ? 'right' : 'left';
            this.overlapChoicePosition = {
              x: (event.native as MouseEvent)?.offsetX ?? 0,
              y: (event.native as MouseEvent)?.offsetY ?? 0,
            };
            this.isOverlapChoiceOpen = true;
            this.isMarketCurveOpen = false;
          } else {
            this.curvePointData = clickedCandidate;
            this.isOverlapChoiceOpen = false;
            this.isMarketCurveOpen = true;
          }
        }
      },
      // TEST: highlight the whole hovered month column
      plugins: [this.columnHighlightPlugin]
    });
    this.chartInstances.push(chartInstance);
  }

  toYearMonthNumber(label: string): number {
    const [month, year] = label.split('-').map(Number);
    return year * 100 + month;  // e.g., '05-2025' → 202505
  }

  private destroyCharts(): void {
    this.chartInstances.forEach(chartInstance => {
      chartInstance.destroy();
    });
    this.chartInstances = [];
  }


  selectOverlappingPoint(candidate: any): void {
    this.curvePointData = candidate;
    this.isOverlapChoiceOpen = false;
    this.overlappingCandidates = [];
    this.onOpenMarketCurveFromWidget(true);
  }

  closeOverlapChoice(): void {
    this.isOverlapChoiceOpen = false;
    this.overlappingCandidates = [];
  }

  onOpenMarketCurveFromWidget(value: boolean) {
    this.isMarketCurveOpen = value;
    if (this.isMarketCurveOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.width = '';
    }
  }

  private createAccumulatedData(dataPoints: DataPoint[], sortedLabels: string[]): Map<string, number> {
    const accumulatedMap = new Map<string, number>();

    // Create a map of the original values
    const originalValues = new Map<string, number>();
    dataPoints.forEach(point => {
      originalValues.set(point.month, point.value);
    });

    // Find the starting point month
    const startingPoint = dataPoints.find(point => point.isStartingPoint);
    if (!startingPoint) {
      return accumulatedMap; // Return empty if no starting point
    }

    const startingMonth = startingPoint.month;
    const startingIndex = sortedLabels.indexOf(startingMonth);

    if (startingIndex === -1) {
      return accumulatedMap; // Return empty if starting month not found in labels
    }

    // Accumulate values starting from the starting point
    let runningTotal = 0;

    for (let i = startingIndex; i < sortedLabels.length; i++) {
      const month = sortedLabels[i];

      if (originalValues.has(month)) {
        runningTotal += originalValues.get(month)!;
        accumulatedMap.set(month, Math.round(runningTotal * 100) / 100); // Round to 2 decimals
      }
    }

    return accumulatedMap;
  }

  private getLastAccumulatedValue(month: string, accumulatedMap: Map<string, number>, sortedLabels: string[]): number | null {
    const currentIndex = sortedLabels.indexOf(month);

    // Look backwards for the last accumulated value
    for (let i = currentIndex - 1; i >= 0; i--) {
      const prevMonth = sortedLabels[i];
      if (accumulatedMap.has(prevMonth)) {
        return accumulatedMap.get(prevMonth)!;
      }
    }

    return null;
  }

  /**
   * Get starting month for a dataset
   */
  private getStartingMonth(dataPoints: DataPoint[]): string | null {
    const startingPoint = dataPoints.find(point => point.isStartingPoint);
    return startingPoint ? startingPoint.month : null;
  }

  /**
   * Get current month in YYYY-MM format
   */
  private getCurrentMonth(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }

  getStartingPoints(apiData: any): StartingPoint[] {
    const startingPoints: StartingPoint[] = [];

    if (apiData.target) {
      const targetStart = apiData.target.find((point: any) => point.isStartingPoint);
      if (targetStart) {
        startingPoints.push({
          type: 'target',
          month: targetStart.month,
          value: targetStart.value
        });
      }
    }

    if (apiData.actual) {
      const actualStart = apiData.actual.find((point: any) => point.isStartingPoint);
      if (actualStart) {
        startingPoints.push({
          type: 'actual',
          month: actualStart.month,
          value: actualStart.value
        });
      }
    }

    return startingPoints;
  }

  private getOverlapThresholdPercent(): number {
    return window.innerWidth < this.OVERLAP_THRESHOLD_BREAKPOINT
      ? this.OVERLAP_THRESHOLD_PERCENT_SMALL
      : this.OVERLAP_THRESHOLD_PERCENT_LARGE;
  }
}